import { useState } from 'react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { detailSectionMessages } from './detail-section-messages';
import styles from './DetailSection.module.css';

import type { ReactNode } from 'react';

type DetailSectionProps = {
  title: string;
  children: ReactNode;
  isDefaultExpanded?: boolean;
  testId?: string;
  /** Optional content rendered on the right side of the header, before the chevron (e.g. a "last updated" timestamp). */
  headerRight?: ReactNode;
};

/**
 * Reusable collapsible card shell shared by every freight detail card.
 *
 * A clickable header (title + chevron) toggles an expandable body. Open/closed
 * is UI-ephemeral state held locally — it carries no domain meaning, so it
 * lives here rather than in any store. Defaults to expanded.
 */
export const DetailSection = ({
  title,
  children,
  isDefaultExpanded = true,
  testId = 'detail-section',
  headerRight,
}: DetailSectionProps) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(isDefaultExpanded);

  const toggleLabel = intl.formatMessage(isExpanded ? detailSectionMessages.collapse : detailSectionMessages.expand, { title });

  return (
    <div className={styles.section} data-testid={testId}>
      <div
        aria-expanded={isExpanded}
        aria-label={toggleLabel}
        className={styles.header}
        onClick={() => setIsExpanded((prev) => !prev)}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (e.key === ' ') e.preventDefault();
            setIsExpanded((prev) => !prev);
          }
        }}
      >
        <span className={styles.title}>{title}</span>
        {headerRight != null && (
          <span className={styles.headerRight} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role='none'>
            {headerRight}
          </span>
        )}
        <FontAwesomeIcon className={styles.chevron} icon={isExpanded ? faChevronUp : faChevronDown} />
      </div>
      {isExpanded && <div className={styles.body}>{children}</div>}
    </div>
  );
};
